<template>
    <v-dialog
        v-model="dialog"
        max-width="900px"
        scrollable
        persistent
        @click:outside="close"
    >
        <v-card>
            <v-card-title>
                <v-btn
                    small
                    icon
                    @click="close"
                    color="primary"
                    class="ml-n3 mr-2"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h2 class="font-weight-regular">Alert Timeline</h2>
            </v-card-title>
            <v-divider />
            <v-card-text style="max-height: 70vh;">
                <v-timeline dense v-if="alert && alert.history">
                    <v-timeline-item
                        v-for="(entry, index) in sortedHistory"
                        :key="index"
                        :color="getActionColor(entry.action)"
                        small
                    >
                        <template v-slot:icon>
                            <v-icon :color="getActionColor(entry.action)" small>
                                {{ getActionIcon(entry.action) }}
                            </v-icon>
                        </template>
                        <v-card class="elevation-2">
                            <v-card-title class="subtitle-2 py-2">
                                <span class="text-capitalize">
                                    {{ formatAction(entry.action) }}
                                </span>
                                <v-spacer />
                                <span class="caption grey--text">
                                    {{ formatDate(entry.timestamp) }}
                                </span>
                            </v-card-title>
                            <v-card-text class="py-2">
                                <div class="mb-2">
                                    <strong>User:</strong> {{ entry.userName }}
                                </div>

                                <div
                                    v-if="
                                        entry.action ===
                                            'forwarded_by_supervisor'
                                    "
                                >
                                    <div
                                        v-if="entry.data && entry.data.notes"
                                        class="mb-1"
                                    >
                                        <strong>Notes:</strong>
                                        {{ entry.data.notes }}
                                    </div>
                                </div>

                                <div v-else-if="entry.data">
                                    <div
                                        v-if="entry.data.alertType"
                                        class="mb-1"
                                    >
                                        <strong>Alert Type:</strong>
                                        {{ entry.data.alertType }}
                                    </div>
                                    <div
                                        v-if="entry.data.classification"
                                        class="mb-1"
                                    >
                                        <strong>Classification:</strong>
                                        {{ entry.data.classification }}
                                    </div>
                                    <div
                                        v-if="entry.data.alertMessage"
                                        class="mb-1"
                                    >
                                        <strong>Message:</strong>
                                        {{ entry.data.alertMessage }}
                                    </div>
                                    <div
                                        v-if="
                                            entry.data.selectedPartNumbers &&
                                                entry.data.selectedPartNumbers
                                                    .length > 0
                                        "
                                        class="mb-1"
                                    >
                                        <strong>Part Numbers:</strong>
                                        <v-chip
                                            v-for="(pn, idx) in entry.data
                                                .selectedPartNumbers"
                                            :key="idx"
                                            small
                                            class="ml-1"
                                        >
                                            {{ pn.partNumber }}
                                        </v-chip>
                                    </div>
                                    <div
                                        v-if="entry.data.addAssemblyComponents"
                                        class="mb-1"
                                    >
                                        <v-chip
                                            color="green"
                                            small
                                            dark
                                            class="text-capitalize"
                                        >
                                            <v-icon small left
                                                >mdi-plus-circle</v-icon
                                            >
                                            Add Assembly Components
                                        </v-chip>
                                    </div>
                                    <div
                                        v-if="
                                            entry.data.removeAssemblyComponents
                                        "
                                        class="mb-1"
                                    >
                                        <v-chip
                                            color="red"
                                            small
                                            dark
                                            class="text-capitalize"
                                        >
                                            <v-icon small left
                                                >mdi-minus-circle</v-icon
                                            >
                                            Remove Assembly Components
                                        </v-chip>
                                    </div>
                                    <div
                                        v-if="entry.data.proposedChanges"
                                        class="mb-1"
                                    >
                                        <strong>Proposed Changes:</strong>
                                        <div class="mt-2">
                                            <v-card
                                                outlined
                                                class="pa-3"
                                                style="background-color: #f5f5f5;"
                                            >
                                                <div
                                                    v-html="
                                                        sanitizedProposedChanges(
                                                            entry.data
                                                                .proposedChanges
                                                        )
                                                    "
                                                ></div>
                                                <div
                                                    v-if="
                                                        getFilesFromChanges(
                                                            entry.data
                                                                .proposedChanges
                                                        ).length > 0
                                                    "
                                                    class="mt-3"
                                                >
                                                    <strong
                                                        >Attached Files:</strong
                                                    >
                                                    <div class="mt-2">
                                                        <v-chip
                                                            v-for="(file,
                                                            idx) in getFilesFromChanges(
                                                                entry.data
                                                                    .proposedChanges
                                                            )"
                                                            :key="idx"
                                                            small
                                                            class="mr-2 mb-2"
                                                            @click="
                                                                openFileFromStorage(
                                                                    file
                                                                )
                                                            "
                                                            style="cursor: pointer;"
                                                        >
                                                            <v-icon small left
                                                                >mdi-file-document-outline</v-icon
                                                            >
                                                            {{ file.name }}
                                                        </v-chip>
                                                    </div>
                                                </div>
                                            </v-card>
                                        </div>
                                    </div>
                                    <div
                                        v-if="entry.data.modifications"
                                        class="mb-1"
                                    >
                                        <strong>Modifications:</strong>
                                        <pre
                                            class="mt-1 pa-2"
                                            style="
                                                background-color: #f5f5f5;
                                                border-radius: 4px;
                                                font-size: 12px;
                                            "
                                            >{{
                                                JSON.stringify(
                                                    entry.data.modifications,
                                                    null,
                                                    2
                                                )
                                            }}</pre
                                        >
                                    </div>
                                    <div
                                        v-if="entry.data.resolutionNotes"
                                        class="mb-1"
                                    >
                                        <strong>Resolution Notes:</strong>
                                        {{ entry.data.resolutionNotes }}
                                    </div>
                                    <div v-if="entry.data.notes" class="mb-1">
                                        <strong>Notes:</strong>
                                        {{ entry.data.notes }}
                                    </div>
                                    <div
                                        v-if="entry.data.nextStep"
                                        class="mb-1"
                                    >
                                        <strong>Next Step:</strong>
                                        {{ entry.data.nextStep }}
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-timeline-item>
                </v-timeline>
                <div v-else class="text-center pa-8">
                    <v-icon large color="grey">mdi-history</v-icon>
                    <p class="mt-4 grey--text">No history available</p>
                </div>
            </v-card-text>
            <v-card-actions class="py-4">
                <v-spacer />
                <v-btn
                    v-if="canEditAlert"
                    color="primary"
                    rounded
                    @click="handleEdit"
                    class="mr-2"
                >
                    EDIT
                </v-btn>
                <v-btn
                    v-if="canResolveAlert"
                    color="primary"
                    rounded
                    @click="handleResolve"
                    class="mr-2"
                >
                    REVIEW ALERT
                </v-btn>
                <v-btn
                    v-if="canMakeChanges"
                    color="primary"
                    rounded
                    :loading="makeChangesOpening"
                    :disabled="makeChangesOpening"
                    @click="handleMakeChanges"
                    class="mr-2"
                >
                    MAKE CHANGES
                </v-btn>
                <v-btn
                    v-if="canApproveAlert"
                    color="primary"
                    rounded
                    @click="handleApprove"
                    class="mr-2"
                >
                    REVIEW ALERT
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import moment from 'moment'
import DOMPurify from 'dompurify'
import { mapMutations } from 'vuex'
import { openFile } from '@/services/storage/openFile.js'
import API from '@/services/api'

export default {
    name: 'AlertTimeline',
    props: {
        alert: {
            type: Object,
            default: () => null,
        },
        value: {
            type: Boolean,
            default: false,
        },
        canEditAlert: {
            type: Boolean,
            default: false,
        },
        canResolveAlert: {
            type: Boolean,
            default: false,
        },
        canMakeChanges: {
            type: Boolean,
            default: false,
        },
        makeChangesOpening: {
            type: Boolean,
            default: false,
        },
        canApproveAlert: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            settingProcesses: [],
            finishAndUCCodes: [],
        }
    },
    computed: {
        dialog: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        sortedHistory() {
            if (!this.alert || !this.alert.history) return []
            return [...this.alert.history].sort((a, b) => {
                const timeA = this.getTimestamp(a.timestamp)
                const timeB = this.getTimestamp(b.timestamp)
                return timeA - timeB
            })
        },
    },
    async mounted() {
        try {
            const {
                data: { settings },
            } = await API.getSettings()
            this.settingProcesses =
                settings.find(s => s.name == 'Processes')?.processes || []
            this.finishAndUCCodes =
                settings.find(s => s.name == 'FinishAndUCCode')?.finishes || []
        } catch (error) {
            console.error('Failed to load settings:', error)
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        toUnixSeconds(timestamp) {
            if (timestamp == null || timestamp === '') return null
            if (typeof timestamp === 'number' && Number.isFinite(timestamp)) {
                return timestamp > 1e12 ? timestamp / 1000 : timestamp
            }
            if (typeof timestamp === 'string' && /^-?\d+(\.\d+)?$/.test(timestamp.trim())) {
                const numeric = Number(timestamp)
                return numeric > 1e12 ? numeric / 1000 : numeric
            }
            if (timestamp instanceof Date) {
                const ms = timestamp.getTime()
                return Number.isNaN(ms) ? null : ms / 1000
            }
            if (typeof timestamp === 'object') {
                if (typeof timestamp.toDate === 'function') {
                    const date = timestamp.toDate()
                    if (date instanceof Date && !Number.isNaN(date.getTime())) {
                        return date.getTime() / 1000
                    }
                }
                const seconds = timestamp.seconds ?? timestamp._seconds
                const numericSeconds = Number(seconds)
                if (seconds != null && seconds !== '' && Number.isFinite(numericSeconds)) {
                    return numericSeconds
                }
            }
            const parsed = moment(timestamp)
            return parsed.isValid() ? parsed.unix() : null
        },
        formatDate(timestamp) {
            const seconds = this.toUnixSeconds(timestamp)
            if (seconds == null) return ''
            return moment.unix(seconds).format('MM/DD/YYYY HH:mm')
        },
        getTimestamp(timestamp) {
            const seconds = this.toUnixSeconds(timestamp)
            return seconds == null ? 0 : seconds
        },
        formatAction(action) {
            if (action === 'auto_created_for_new_part_numbers') {
                return 'Alert created for adding assembly items'
            }
            if (action === 'auto_forwarded_on_create') {
                return 'Auto forwarded to PM/Designer'
            }
            if (action === 'resolved_without_apply') {
                return 'Resolved without applying changes'
            }
            return action
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
        },
        getActionColor(action) {
            const colors = {
                created: 'blue',
                resolved_by_supervisor: 'green',
                forwarded_by_supervisor: 'orange',
                auto_forwarded_on_create: 'orange',
                changes_made: 'purple',
                approved_by_supervisor: 'green',
                rejected_by_supervisor: 'red',
                changes_applied: 'teal',
                auto_created_for_new_part_numbers: 'blue',
            }
            return colors[action] || 'grey'
        },
        getActionIcon(action) {
            const icons = {
                created: 'mdi-plus-circle',
                resolved_by_supervisor: 'mdi-check-circle',
                forwarded_by_supervisor: 'mdi-arrow-forward',
                auto_forwarded_on_create: 'mdi-arrow-forward',
                changes_made: 'mdi-pencil',
                approved_by_supervisor: 'mdi-check',
                rejected_by_supervisor: 'mdi-close-circle',
                resolved_without_apply: 'mdi-archive-check',
                changes_applied: 'mdi-check-all',
                auto_created_for_new_part_numbers: 'mdi-package-variant-closed',
            }
            return icons[action] || 'mdi-circle'
        },
        close() {
            this.$emit('input', false)
        },
        handleEdit() {
            this.$emit('edit', this.alert)
        },
        handleResolve() {
            this.$emit('resolve', this.alert)
        },
        handleMakeChanges() {
            this.$emit('make-changes', this.alert)
        },
        handleApprove() {
            this.$emit('approve', this.alert)
        },
        /** WO partNumbers key; resolve to human-readable part number for timeline */
        parentAssemblyPartLabel(partNumberId) {
            if (!partNumberId) return ''
            const line = this.alert?.workOrder?.partNumbers?.[partNumberId]
            if (line) {
                const code =
                    line.partNumber || line.code || line.reference || line.id
                if (code) return String(code)
            }
            const sel = (this.alert?.selectedPartNumbers || []).find(
                p => p.id === partNumberId
            )
            if (sel?.partNumber) return String(sel.partNumber)
            return String(partNumberId)
        },
        sanitizedProposedChanges(changes) {
            return DOMPurify.sanitize(this.formatProposedChanges(changes))
        },
        formatProposedChanges(changes) {
            if (!changes) return '<em style="color: #9e9e9e;">No changes</em>'

            const type = changes.type
            let html = ''

            const badge = (text, color) =>
                `<span style="display: inline-block; padding: 4px 12px; background-color: ${color}; color: white; border-radius: 16px; font-size: 11px; font-weight: bold; margin-bottom: 8px;">${text}</span>`

            switch (type) {
                case 'add':
                    html +=
                        '<div class="mb-2">' +
                        badge('ADD ITEMS', '#4caf50') +
                        '</div>'
                    html +=
                        '<div class="mb-2">' +
                        badge(
                            'For this alert, it is only possible to add the main item once the change has been accepted. If it belongs to an assembly work order, an additional alert will be automatically created to add the components.',
                            '#2b81d6'
                        ) +
                        '</div>'
                    if (changes.items && Array.isArray(changes.items)) {
                        html +=
                            '<div class="mb-2"><strong>Items to add:</strong> ' +
                            changes.items.length +
                            '</div>'
                        html += '<div style="margin-left: 0px;">'
                        changes.items.forEach(item => {
                            html +=
                                '<div class="mb-3" style="background-color: #fafafa; padding: 16px; border-radius: 8px; border-left: 4px solid #4caf50;">'

                            html +=
                                '<div style="margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #4caf50;">'
                            html +=
                                '<strong style="font-size: 15px; color: #388e3c;">Part Number:</strong> '
                            html +=
                                '<span style="font-size: 15px; font-weight: 600; color: #4caf50;">' +
                                (item.partNumber || 'N/A') +
                                '</span>'
                            html += '</div>'

                            html +=
                                '<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">'
                            if (item.description) {
                                html +=
                                    '<div><strong>Description:</strong><br><span style="color: #616161;">' +
                                    item.description +
                                    '</span></div>'
                            }
                            if (item.quantity) {
                                html +=
                                    '<div><strong>Quantity:</strong><br><span style="color: #616161;">' +
                                    item.quantity +
                                    '</span></div>'
                            }
                            if (item.mass) {
                                html +=
                                    '<div><strong>Mass (Kg):</strong><br><span style="color: #616161;">' +
                                    item.mass +
                                    '</span></div>'
                            }
                            if (item.paintingArea) {
                                html +=
                                    '<div><strong>Painting Area (m²):</strong><br><span style="color: #616161;">' +
                                    item.paintingArea +
                                    '</span></div>'
                            }
                            if (item.pieceLength) {
                                html +=
                                    '<div><strong>Piece Length (mm):</strong><br><span style="color: #616161;">' +
                                    Number(item.pieceLength).toFixed(1) +
                                    '</span></div>'
                            }
                            if (item.pieceWidth) {
                                html +=
                                    '<div><strong>Piece Width (mm):</strong><br><span style="color: #616161;">' +
                                    Number(item.pieceWidth).toFixed(1) +
                                    '</span></div>'
                            }
                            html += '</div>'

                            if (
                                item.finishAndUCCode ||
                                item.dataFinishAndUCCode
                            ) {
                                html +=
                                    '<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e0e0;">'
                                html +=
                                    '<div><strong>Finish & UC Code:</strong><br><span style="color: #616161;">'

                                if (
                                    item.dataFinishAndUCCode &&
                                    item.dataFinishAndUCCode.name
                                ) {
                                    html += item.dataFinishAndUCCode.name
                                } else if (
                                    typeof item.finishAndUCCode === 'object' &&
                                    item.finishAndUCCode.name
                                ) {
                                    html += item.finishAndUCCode.name
                                } else if (item.finishAndUCCode) {
                                    html +=
                                        this.finishAndUCCodes.find(
                                            f => f.id === item.finishAndUCCode
                                        )?.name || item.finishAndUCCode
                                }

                                html += '</span></div>'
                                html += '</div>'
                            }

                            if (item.processes && item.processes.length > 0) {
                                html +=
                                    '<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e0e0;">'
                                html += '<div><strong>Processes:</strong><br>'
                                html += '<span style="color: #616161;">'
                                html += this.formatProcessesForDisplay(
                                    item.processes
                                )
                                html += '</span>'
                                html += '</div>'
                                html += '</div>'
                            }

                            html += '</div>'
                        })
                        html += '</div>'
                    }
                    break

                case 'edit':
                    html +=
                        '<div class="mb-2">' +
                        badge('EDIT ITEMS', '#2196f3') +
                        '</div>'
                    if (changes.edits && Array.isArray(changes.edits)) {
                        html +=
                            '<div class="mb-2"><strong>Items to edit:</strong> ' +
                            changes.edits.length +
                            '</div>'
                        html += '<div style="margin-left: 0px;">'
                        changes.edits.forEach(edit => {
                            html +=
                                '<div class="mb-3" style="background-color: #fafafa; padding: 16px; border-radius: 8px; border-left: 4px solid #2196f3;">'

                            const partNumberCode =
                                edit.partNumberCode ||
                                (edit.item && edit.item.partNumber) ||
                                edit.partNumberId
                            html +=
                                '<div style="margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #2196f3;">'
                            html +=
                                '<strong style="font-size: 15px; color: #1976d2;">Part Number:</strong> '
                            html +=
                                '<span style="font-size: 15px; font-weight: 600; color: #2196f3;">' +
                                partNumberCode +
                                '</span>'
                            html += '</div>'

                            if (edit.item) {
                                html +=
                                    '<div style="margin-bottom: 12px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">'
                                if (edit.item.description) {
                                    html +=
                                        '<div><strong>Description:</strong><br><span style="color: #616161;">' +
                                        edit.item.description +
                                        '</span></div>'
                                }
                                if (edit.item.quantity) {
                                    html +=
                                        '<div><strong>Quantity:</strong><br><span style="color: #616161;">' +
                                        edit.item.quantity +
                                        '</span></div>'
                                }
                                if (edit.item.mass) {
                                    html +=
                                        '<div><strong>Mass (Kg):</strong><br><span style="color: #616161;">' +
                                        edit.item.mass +
                                        '</span></div>'
                                }
                                if (edit.item.paintingArea) {
                                    html +=
                                        '<div><strong>Painting Area (m²):</strong><br><span style="color: #616161;">' +
                                        edit.item.paintingArea +
                                        '</span></div>'
                                }
                                if (edit.item.pieceLength) {
                                    html +=
                                        '<div><strong>Piece Length (mm):</strong><br><span style="color: #616161;">' +
                                        Number(edit.item.pieceLength).toFixed(
                                            1
                                        ) +
                                        '</span></div>'
                                }
                                if (edit.item.pieceWidth) {
                                    html +=
                                        '<div><strong>Piece Width (mm):</strong><br><span style="color: #616161;">' +
                                        Number(edit.item.pieceWidth).toFixed(
                                            1
                                        ) +
                                        '</span></div>'
                                }
                                if (
                                    edit.item.finishAndUCCode ||
                                    edit.item.dataFinishAndUCCode
                                ) {
                                    html +=
                                        '<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e0e0;">'
                                    html +=
                                        '<div><strong>Finish & UC Code:</strong><br><span style="color: #616161;">'

                                    if (
                                        edit.item.dataFinishAndUCCode &&
                                        edit.item.dataFinishAndUCCode.name
                                    ) {
                                        html +=
                                            edit.item.dataFinishAndUCCode.name
                                    } else if (
                                        typeof edit.item.finishAndUCCode ===
                                            'object' &&
                                        edit.item.finishAndUCCode.name
                                    ) {
                                        html += edit.item.finishAndUCCode.name
                                    } else if (edit.item.finishAndUCCode) {
                                        html +=
                                            this.finishAndUCCodes.find(
                                                f =>
                                                    f.id ===
                                                    edit.item.finishAndUCCode
                                            )?.name || edit.item.finishAndUCCode
                                    }

                                    html += '</span></div>'
                                    html += '</div>'
                                }

                                if (
                                    edit.item.processes &&
                                    edit.item.processes.length > 0
                                ) {
                                    html +=
                                        '<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e0e0;">'
                                    html +=
                                        '<div><strong>Processes:</strong><br>'
                                    html += '<span style="color: #616161;">'
                                    html += this.formatProcessesForDisplay(
                                        edit.item.processes
                                    )
                                    html += '</span>'
                                    html += '</div>'
                                    html += '</div>'
                                }
                                html += '</div>'
                            }

                            {
                                const diffKeys =
                                    edit.itemDiff &&
                                    Object.keys(edit.itemDiff).length > 0
                                        ? Object.keys(edit.itemDiff).filter(
                                              key =>
                                                  key !== 'processes' &&
                                                  key !== 'dataProcesses'
                                          )
                                        : []
                                const hasProcessIds =
                                    edit.processesChanged === true &&
                                    edit.processIds !== undefined &&
                                    edit.processIds !== null
                                if (diffKeys.length > 0 || hasProcessIds) {
                                    html +=
                                        '<div style="margin-top: 12px; padding: 12px; background-color: #fff3e0; border-radius: 4px;">'
                                    html +=
                                        '<strong style="color: #f57c00;">Changes Made:</strong><br>'
                                    html +=
                                        '<ul style="margin-left: 15px; margin-top: 6px;">'
                                    diffKeys.forEach(key => {
                                        const displayKey =
                                            key.charAt(0).toUpperCase() +
                                            key
                                                .slice(1)
                                                .replace(/([A-Z])/g, ' $1')
                                        let displayValue
                                        if (
                                            key === 'processes' &&
                                            Array.isArray(edit.itemDiff[key])
                                        ) {
                                            const formatted = this.formatProcessesForDisplay(
                                                edit.itemDiff[key]
                                            )
                                            displayValue =
                                                formatted ||
                                                this.formatValue(
                                                    edit.itemDiff[key]
                                                )
                                        } else {
                                            displayValue = this.formatValue(
                                                edit.itemDiff[key]
                                            )
                                        }
                                        html +=
                                            '<li style="margin-bottom: 6px;"><strong>' +
                                            displayKey +
                                            ':</strong> <span style="color: #f57c00; font-weight: 500;">' +
                                            displayValue +
                                            '</span></li>'
                                    })
                                    if (hasProcessIds) {
                                        const procDisplay =
                                            this.formatProcessesForDisplay(
                                                edit.processIds
                                            ) ||
                                            this.formatValue(edit.processIds)
                                        html +=
                                            '<li style="margin-bottom: 6px;"><strong>Processes:</strong> <span style="color: #f57c00; font-weight: 500;">' +
                                            procDisplay +
                                            '</span></li>'
                                    }
                                    html += '</ul>'
                                    html += '</div>'
                                }
                            }
                            html += '</div>'
                        })
                        html += '</div>'
                    }
                    break

                case 'remove':
                    html +=
                        '<div class="mb-2">' +
                        badge('REMOVE ITEMS', '#f44336') +
                        '</div>'
                    if (changes.rejected === true) {
                        html +=
                            '<div class="mb-2"><strong>Action:</strong> Removal was cancelled</div>'
                    } else {
                        html +=
                            '<div class="mb-2"><strong>Action:</strong> Remove selected part numbers</div>'
                    }
                    if (changes.accepted !== undefined) {
                        html +=
                            '<div><strong>Status:</strong> ' +
                            (changes.accepted ? 'Accepted' : 'Pending') +
                            '</div>'
                    }
                    break

                case 'addAssembly':
                    html +=
                        '<div class="mb-2">' +
                        badge('ADD ASSEMBLY ITEMS', '#4caf50') +
                        '</div>'
                    if (
                        changes.assemblyItems &&
                        Array.isArray(changes.assemblyItems)
                    ) {
                        html +=
                            '<div class="mb-2"><strong>Assembly items to add:</strong> ' +
                            changes.assemblyItems.length +
                            '</div>'
                        html += '<ul style="margin-left: 20px;">'
                        changes.assemblyItems.forEach(item => {
                            html += '<li class="mb-1">'
                            html +=
                                '<strong>' +
                                item.partNumber +
                                '</strong> - Qty per assembly: ' +
                                item.qtyPerAssembly
                            html += '</li>'
                        })
                        html += '</ul>'
                    }
                    break

                case 'removeAssembly':
                    html +=
                        '<div class="mb-2">' +
                        badge('REMOVE ASSEMBLY ITEMS', '#f44336') +
                        '</div>'
                    if (
                        changes.assemblyItemsToRemove &&
                        Array.isArray(changes.assemblyItemsToRemove)
                    ) {
                        html +=
                            '<div class="mb-2"><strong>Assembly items to remove:</strong> ' +
                            changes.assemblyItemsToRemove.length +
                            '</div>'
                        html += '<ul style="margin-left: 20px;">'
                        changes.assemblyItemsToRemove.forEach(item => {
                            html += '<li class="mb-1">'
                            html +=
                                '<strong>' +
                                item.partNumber +
                                '</strong> - Qty: ' +
                                (item.qtyPerAssembly || 'N/A')
                            html += '</li>'
                        })
                        html += '</ul>'
                    }
                    break

                case 'editAssembly': {
                    html +=
                        '<div class="mb-2">' +
                        badge('EDIT ASSEMBLY ITEMS', '#ff9800') +
                        '</div>'
                    const assemblyBlocks =
                        Array.isArray(changes.assemblyEdits) &&
                        changes.assemblyEdits.length > 0
                            ? changes.assemblyEdits
                            : changes.assemblyChanges
                            ? [
                                  {
                                      partNumberId: changes.partNumberId,
                                      assemblyChanges: changes.assemblyChanges,
                                  },
                              ]
                            : []
                    const renderAssemblyDelta = ac => {
                        if (!ac) return ''
                        let h = ''
                        if (ac.itemsToAdd && ac.itemsToAdd.length > 0) {
                            h +=
                                '<div class="mb-2"><strong style="color: #4caf50;">Items Added:</strong></div>'
                            h += '<ul style="margin-left: 20px;">'
                            ac.itemsToAdd.forEach(item => {
                                h += '<li class="mb-1">'
                                h +=
                                    '<strong>' +
                                    item.partNumber +
                                    '</strong> - Qty per assembly: ' +
                                    item.qtyPerAssembly
                                h += '</li>'
                            })
                            h += '</ul>'
                        }
                        if (ac.itemsToUpdate && ac.itemsToUpdate.length > 0) {
                            h +=
                                '<div class="mb-2 mt-3"><strong style="color: #2196f3;">Items Updated:</strong></div>'
                            h += '<ul style="margin-left: 20px;">'
                            ac.itemsToUpdate.forEach(item => {
                                h += '<li class="mb-1">'
                                h +=
                                    '<strong>' +
                                    item.partNumber +
                                    '</strong> - Qty changed from ' +
                                    item.oldQtyPerAssembly +
                                    ' to ' +
                                    item.qtyPerAssembly
                                h += '</li>'
                            })
                            h += '</ul>'
                        }
                        if (ac.itemsToRemove && ac.itemsToRemove.length > 0) {
                            h +=
                                '<div class="mb-2 mt-3"><strong style="color: #f44336;">Items Removed:</strong></div>'
                            h += '<ul style="margin-left: 20px;">'
                            ac.itemsToRemove.forEach(item => {
                                h += '<li class="mb-1">'
                                h += '<strong>' + item.partNumber + '</strong>'
                                h += '</li>'
                            })
                            h += '</ul>'
                        }
                        return h
                    }
                    assemblyBlocks.forEach(block => {
                        if (block.partNumberId) {
                            const parentLabel = this.parentAssemblyPartLabel(
                                block.partNumberId
                            )
                            html +=
                                '<div class="mb-2 mt-2"><strong>Parent assembly line:</strong> ' +
                                parentLabel
                            if (parentLabel !== String(block.partNumberId)) {
                                html +=
                                    ' <span style="color: #9e9e9e; font-size: 12px;"></span>'
                            }
                            html += '</div>'
                        }
                        html += renderAssemblyDelta(block.assemblyChanges)
                    })
                    break
                }

                default:
                    html +=
                        '<pre style="font-size: 12px; white-space: pre-wrap;">' +
                        JSON.stringify(changes, null, 2) +
                        '</pre>'
            }

            if (changes.message) {
                html +=
                    '<div class="mt-3"><strong>Additional Message:</strong><br><em>' +
                    changes.message +
                    '</em></div>'
            }

            return html
        },
        formatValue(value) {
            if (value === null || value === undefined) return 'N/A'
            if (typeof value === 'object') {
                if (Array.isArray(value)) {
                    if (value.length === 0) return 'None'
                    if (
                        typeof value[0] === 'object' &&
                        value[0] !== null &&
                        value[0].name
                    ) {
                        return value.map(v => v?.name ?? '').join(', ')
                    }
                    return value.join(', ')
                }
                if (value.name) return value.name
                return JSON.stringify(value)
            }
            return String(value)
        },
        /** Resolve process IDs/objects to display names (settingProcesses, object.name, string). */
        formatProcessesForDisplay(processes) {
            if (!Array.isArray(processes) || processes.length === 0) return ''
            const names = []
            for (const process of processes) {
                const processData = this.settingProcesses.find(
                    p => p.id === process
                )
                if (processData) {
                    names.push(processData.name)
                } else if (
                    typeof process === 'object' &&
                    process &&
                    process.name
                ) {
                    names.push(process.name)
                } else if (typeof process === 'string') {
                    names.push(process)
                } else if (process) {
                    names.push(String(process))
                }
            }
            return names.join(', ')
        },
        getFilesFromChanges(changes) {
            if (!changes) return []

            const files = []

            if (changes.type === 'add' && changes.items) {
                changes.items.forEach(item => {
                    if (item.files && Array.isArray(item.files)) {
                        item.files.forEach(file => {
                            files.push({
                                ...file,
                                partNumber:
                                    item.partNumber || item.partNumberId,
                            })
                        })
                    }
                })
            }

            if (changes.type === 'edit' && changes.edits) {
                changes.edits.forEach(edit => {
                    if (edit.files && Array.isArray(edit.files)) {
                        edit.files.forEach(file => {
                            files.push({
                                ...file,
                                partNumber:
                                    edit.partNumberCode ||
                                    (edit.item && edit.item.partNumber) ||
                                    edit.partNumberId,
                            })
                        })
                    }
                })
            }

            return files
        },
        async openFileFromStorage(file) {
            try {
                const companyId =
                    JSON.parse(localStorage.getItem('company') || '{}').id ||
                    JSON.parse(localStorage.getItem('company'))
                let storagePath = ''
                let fileName = ''

                // 1) Si viene con url completa (con ruta), úsala tal cual
                if (file.url && file.url.includes('/')) {
                    const pathParts = file.url.split('/')
                    storagePath = pathParts.slice(0, -1).join('/')
                    fileName = pathParts[pathParts.length - 1]
                }
                // 2) Si tenemos name (caso archivos nuevos agregados durante edición),
                //    arma la ruta a partir del alert/workOrder y el nombre
                else if (file.name) {
                    fileName = file.name
                    if (
                        this.alert &&
                        this.alert.workOrderId &&
                        file.id == file.name
                    ) {
                        storagePath = `${companyId}/workOrders/${this.alert.workOrderId}/items`
                    } else if (this.alert && this.alert.id) {
                        storagePath = `${companyId}/alerts/${this.alert.id}/items`
                    }
                }
                // 3) Fallback: usar id, permitiendo tanto id con ruta completa como solo nombre
                else if (file.id) {
                    if (file.id.includes('/')) {
                        const lastSlashIndex = file.id.lastIndexOf('/')
                        storagePath = file.id.substring(0, lastSlashIndex)
                        fileName = file.id.substring(lastSlashIndex + 1)
                    } else {
                        fileName = file.id
                        if (this.alert && this.alert.workOrderId) {
                            storagePath = `${companyId}/workOrders/${this.alert.workOrderId}/items`
                        } else if (this.alert && this.alert.id) {
                            storagePath = `${companyId}/alerts/${this.alert.id}/items`
                        }
                    }
                }

                if (!fileName || !storagePath) {
                    throw new Error('Unable to determine file path')
                }

                await openFile(fileName, storagePath)
            } catch (error) {
                console.error('Error opening file:', error)
                this.setErrorItems({
                    source: this.$options.name,
                    message: 'Error opening file: ' + error.message,
                })
            }
        },
    },
}
</script>

<style scoped></style>
